import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSetAtom, useAtomValue } from 'jotai';
import { atom } from 'jotai';
import { useCallback } from 'react';
import { QUERY_KEYS, useAnalytics } from '@restworld/utils-common';
import { useJobPositionsService, useEmployersService } from '@restworld/data-services';
import { useTranslation } from 'next-i18next';
import { showToast } from '@restworld/ui-ds-base';
import { useAuth } from '@restworld/feature-authentication';
import {
  useIsSubscriptionFeedbacksSubmitted,
  useFetchIsSubscriptionFeedbacksSubmittedQuery,
  isSubscriptionFeedbacksSubmittedQueryKey,
} from '../../flexible-slots-v2/flexible-slots-v2.hooks';

export const putOfflineJobPositionConfirmationModalAtom = atom<boolean>(false);
export const putOfflineJobPositionNPSModalAtom = atom<boolean>(false);
export const putOfflineJobPositionSuccessModalAtom = atom<boolean>(false);
export const putOfflineJobPositionRecoveryModalAtom = atom<boolean>(false);
export const putOfflineJobPositionReferralModalAtom = atom<boolean>(false);
export const jobPositionIdToPutOfflineAtom = atom<string | null>('');
// 'now' puts the JP offline right away; 'end_of_covered_day' keeps it online until the PPU paid period expires.
export type PutOfflineMode = 'now' | 'end_of_covered_day';
export const putOfflineModeAtom = atom<PutOfflineMode>('now' as PutOfflineMode);
// Boundary returned by the backend when going offline is scheduled at the end of the paid period.
// The cast keeps this a writable primitive atom: without strictNullChecks a bare `null` matches the read-only overload.
export const scheduledOfflineAtAtom = atom<string | null>(null as string | null);

// NPS score bands: 1-6 → Recovery, 7-8 → standard success, 9-10 → Referral
const NPS_MOTIVE_MAX = 6;
const NPS_REFERRAL_MIN = 9;

export interface NPSFeedbackData {
  npsScore: number;
  motiveForCancelling?: string;
  additionalFeedback?: string;
  reactivationDate?: string;
}

const NPS_OFFLINE_DELAY_SECONDS = 60;

// The data-services axios interceptor reshapes a failed request into `{ code, message, data }`,
// where `code` is the HTTP status.
const HTTP_CONFLICT = 409;
export const isConflictError = (error: unknown) => (error as { code?: unknown } | null)?.code === HTTP_CONFLICT;

export function usePutOfflineJobPosition() {
  const queryClient = useQueryClient();
  const jobPositionsService = useJobPositionsService();
  const employersService = useEmployersService();
  const { t } = useTranslation('job-positions-page');
  const { user } = useAuth();
  const { trackEvent } = useAnalytics();

  const setIsConfirmationModalOpen = useSetAtom(putOfflineJobPositionConfirmationModalAtom);
  const setIsNPSModalOpen = useSetAtom(putOfflineJobPositionNPSModalAtom);
  const setIsSuccessModalOpen = useSetAtom(putOfflineJobPositionSuccessModalAtom);
  const setIsRecoveryModalOpen = useSetAtom(putOfflineJobPositionRecoveryModalAtom);
  const setIsReferralModalOpen = useSetAtom(putOfflineJobPositionReferralModalAtom);
  const setJpIdToPutOffline = useSetAtom(jobPositionIdToPutOfflineAtom);
  const isConfirmationModalOpen = useAtomValue(putOfflineJobPositionConfirmationModalAtom);
  const isNPSModalOpen = useAtomValue(putOfflineJobPositionNPSModalAtom);
  const isSuccessModalOpen = useAtomValue(putOfflineJobPositionSuccessModalAtom);
  const isRecoveryModalOpen = useAtomValue(putOfflineJobPositionRecoveryModalAtom);
  const isReferralModalOpen = useAtomValue(putOfflineJobPositionReferralModalAtom);
  const jpIdToPutOffline = useAtomValue(jobPositionIdToPutOfflineAtom);
  const putOfflineMode = useAtomValue(putOfflineModeAtom);
  const setPutOfflineMode = useSetAtom(putOfflineModeAtom);
  const scheduledOfflineAt = useAtomValue(scheduledOfflineAtAtom);
  const setScheduledOfflineAt = useSetAtom(scheduledOfflineAtAtom);

  // Fetch JP details to get ppu_paid_until when the confirmation modal is open
  // A backend without core-services#710 would put the job ad offline right away on "when it expires":
  // the choice is offered only once the backend says it supports it.
  const { data: supportsScheduledOffline = false, isLoading: isLoadingSupport } = useQuery({
    queryKey: ['backend-supports-scheduled-offline'],
    queryFn: () => jobPositionsService.supportsScheduledOffline(),
    enabled: isConfirmationModalOpen,
    staleTime: Infinity,
  });

  const { data: jpDetailsData, isLoading: isLoadingDetails } = useQuery({
    queryKey: [QUERY_KEYS.JOB_POSITIONS.DETAILS, jpIdToPutOffline, 'ppu_paid_until'],
    queryFn: () => jobPositionsService.showJobPosition(jpIdToPutOffline as string),
    enabled: !!jpIdToPutOffline && isConfirmationModalOpen,
    staleTime: 60 * 1000,
  });

  const isLoadingPaidUntil = isLoadingDetails || isLoadingSupport;
  const ppuPaidUntil = jpDetailsData?.data?.ppu_paid_until ?? null;
  const ppuOfflineScheduledAt = jpDetailsData?.data?.ppu_offline_scheduled_at ?? null;

  // Fetch subscription feedback status when confirmation modal is open
  useFetchIsSubscriptionFeedbacksSubmittedQuery();

  const isSubscriptionFeedbacksSubmitted = useIsSubscriptionFeedbacksSubmitted();

  // Put JP offline mutation — supports optional delay

  const { mutate: putOfflineJobPosition, isPending: isPuttingOffline } = useMutation({
    mutationFn: ({ jobPositionId, delaySeconds }: { jobPositionId: string; delaySeconds?: number }) =>
      jobPositionsService.putOfflineJobPosition(jobPositionId, delaySeconds),
    onSuccess: (_data, variables) => {
      trackEvent('employer_jp_goes_offline', {
        jp_id: variables.jobPositionId,
        organization_id: user?.organization_id,
        had_nps_delay: !!variables.delaySeconds,
      });
      if (!variables.delaySeconds) {
        // Immediate offline — invalidate queries to refresh JP status
        queryClient.invalidateQueries({
          queryKey: [QUERY_KEYS.JOB_POSITIONS.LIST],
        });
      }
      setIsConfirmationModalOpen(false);
    },
    onError: () => {
      showToast(t('put_offline_error_message'), 'error', {
        position: 'top-right',
        autoClose: 3000,
      });
      setIsConfirmationModalOpen(false);
      setIsNPSModalOpen(false);
    },
  });

  // Schedule the JP to go offline when its PPU paid period expires (no further charges)
  const { mutateAsync: scheduleOffline, isPending: isSchedulingOffline } = useMutation({
    mutationFn: ({ jobPositionId, expectedPaidUntil }: { jobPositionId: string; expectedPaidUntil: string }) =>
      jobPositionsService.scheduleOfflineJobPositionAtPeriodEnd(jobPositionId, expectedPaidUntil),
    onSuccess: (response, variables) => {
      setScheduledOfflineAt(response.data.ppu_offline_scheduled_at);
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.JOB_POSITIONS.LIST] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.JOB_POSITIONS.DETAILS, variables.jobPositionId] });
      trackEvent('employer_jp_offline_scheduled', {
        jp_id: variables.jobPositionId,
        organization_id: user?.organization_id,
      });
    },
    onError: (error, variables) => {
      // Keep the confirmation modal open: the user can still choose to put it offline now.
      if (isConflictError(error)) {
        // The paid day renewed since the modal was opened: refetch details (including the modal's
        // ppu_paid_until query, which shares the key prefix) so the modal shows the new time.
        showToast(t('scheduled_offline_paid_until_changed'), 'error', {
          position: 'top-right',
          autoClose: 5000,
        });
        queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.JOB_POSITIONS.DETAILS, variables.jobPositionId] });
        return;
      }
      showToast(t('scheduled_offline_error'), 'error', {
        position: 'top-right',
        autoClose: 3000,
      });
    },
  });

  // Submit NPS feedback mutation
  const { mutate: submitNPSFeedback, isPending: isSubmittingFeedback } = useMutation({
    mutationFn: async (feedbackData: NPSFeedbackData) => {
      return employersService.createEmployerSubscriptionFeedback({
        organization_id: user?.organization_id,
        service_referral_rating: feedbackData.npsScore.toString(),
        motive_for_cancelling_subscription: feedbackData.motiveForCancelling,
        additional_feedback: feedbackData.additionalFeedback,
        reactivate_service: feedbackData.reactivationDate,
      });
    },
    onSuccess: (_data, variables) => {
      // Mark the feedback cooldown as active so subsequent offline flows in the same session
      // skip the NPS modal (backend gates re-prompt for 7 days).
      queryClient.setQueryData(isSubscriptionFeedbacksSubmittedQueryKey, true);
      queryClient.invalidateQueries({ queryKey: isSubscriptionFeedbacksSubmittedQueryKey });

      // NPS submitted — now put JP offline immediately (cancels the scheduled delayed job).
      // When going offline is scheduled at the end of the paid period, the JP must stay online.
      if (jpIdToPutOffline && putOfflineMode !== 'end_of_covered_day') {
        putOfflineJobPosition({ jobPositionId: jpIdToPutOffline });
      }
      setIsNPSModalOpen(false);

      // Branch by score: Recovery (1-6), Referral (9-10), or standard success (7-8).
      if (variables.npsScore <= NPS_MOTIVE_MAX) {
        setIsRecoveryModalOpen(true);
      } else if (variables.npsScore >= NPS_REFERRAL_MIN) {
        setIsReferralModalOpen(true);
      } else {
        setIsSuccessModalOpen(true);
      }
    },
    onError: () => {
      showToast(t('nps_feedback_error_message'), 'error', {
        position: 'top-right',
        autoClose: 3000,
      });
    },
  });

  const handleOpenPutOffline = useCallback(
    (jpId: string) => {
      setJpIdToPutOffline(jpId);
      setIsConfirmationModalOpen(true);
    },
    [setJpIdToPutOffline, setIsConfirmationModalOpen]
  );

  // Step 1: User confirms they want to put offline
  const handleConfirmPutOffline = useCallback(() => {
    if (!jpIdToPutOffline) return;
    setPutOfflineMode('now');

    if (isSubscriptionFeedbacksSubmitted) {
      // NPS already submitted recently — put offline immediately, no delay needed
      putOfflineJobPosition({ jobPositionId: jpIdToPutOffline });
      setIsSuccessModalOpen(true);
    } else {
      // Schedule delayed offline (60s safety net) and show NPS modal
      putOfflineJobPosition({
        jobPositionId: jpIdToPutOffline,
        delaySeconds: NPS_OFFLINE_DELAY_SECONDS,
      });
      setIsConfirmationModalOpen(false);
      setIsNPSModalOpen(true);
    }
  }, [
    isSubscriptionFeedbacksSubmitted,
    jpIdToPutOffline,
    putOfflineJobPosition,
    setIsConfirmationModalOpen,
    setIsNPSModalOpen,
    setIsSuccessModalOpen,
    setPutOfflineMode,
  ]);

  // Step 1 (alternative): keep the JP online until the paid period expires, then it goes offline on its own.
  // No delayed offline job here: the backend schedules it.
  const handleConfirmScheduledPutOffline = useCallback(async () => {
    if (!jpIdToPutOffline || !ppuPaidUntil) return;
    setPutOfflineMode('end_of_covered_day');

    try {
      await scheduleOffline({ jobPositionId: jpIdToPutOffline, expectedPaidUntil: ppuPaidUntil });
    } catch {
      // Error toast is shown by the mutation; the confirmation modal stays open.
      return;
    }

    setIsConfirmationModalOpen(false);
    if (isSubscriptionFeedbacksSubmitted) {
      setIsSuccessModalOpen(true);
    } else {
      setIsNPSModalOpen(true);
    }
  }, [
    isSubscriptionFeedbacksSubmitted,
    jpIdToPutOffline,
    ppuPaidUntil,
    scheduleOffline,
    setIsConfirmationModalOpen,
    setIsNPSModalOpen,
    setIsSuccessModalOpen,
    setPutOfflineMode,
  ]);

  const resetPutOfflineFlow = useCallback(() => {
    setJpIdToPutOffline(null);
    setPutOfflineMode('now');
    setScheduledOfflineAt(null);
  }, [setJpIdToPutOffline, setPutOfflineMode, setScheduledOfflineAt]);

  // Step 2: User submits NPS feedback
  const handleSubmitNPSFeedback = useCallback(
    (feedbackData: NPSFeedbackData) => {
      submitNPSFeedback(feedbackData);
    },
    [submitNPSFeedback]
  );

  const handleCloseConfirmationModal = useCallback(() => {
    if (!isPuttingOffline && !isSchedulingOffline) {
      setIsConfirmationModalOpen(false);
      resetPutOfflineFlow();
    }
  }, [isPuttingOffline, isSchedulingOffline, setIsConfirmationModalOpen, resetPutOfflineFlow]);

  const handleCloseNPSModal = useCallback(() => {
    if (!isSubmittingFeedback) {
      // JP is NOT offline yet (delayed job scheduled) — user goes back to list
      // They'll see JP still online, incentivizing them to come back and complete NPS
      setIsNPSModalOpen(false);
      resetPutOfflineFlow();
    }
  }, [isSubmittingFeedback, setIsNPSModalOpen, resetPutOfflineFlow]);

  const handleCloseSuccessModal = useCallback(() => {
    const jobPositionId = jpIdToPutOffline;
    setIsSuccessModalOpen(false);
    resetPutOfflineFlow();
    if (jobPositionId) {
      void queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.JOB_POSITIONS.DETAILS, jobPositionId],
        exact: true,
      });
    }
  }, [jpIdToPutOffline, queryClient, setIsSuccessModalOpen, resetPutOfflineFlow]);

  const handleCloseRecoveryModal = useCallback(() => {
    const jobPositionId = jpIdToPutOffline;
    setIsRecoveryModalOpen(false);
    resetPutOfflineFlow();
    if (jobPositionId) {
      void queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.JOB_POSITIONS.DETAILS, jobPositionId],
        exact: true,
      });
    }
  }, [jpIdToPutOffline, queryClient, setIsRecoveryModalOpen, resetPutOfflineFlow]);

  const handleCloseReferralModal = useCallback(() => {
    const jobPositionId = jpIdToPutOffline;
    setIsReferralModalOpen(false);
    resetPutOfflineFlow();
    if (jobPositionId) {
      void queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.JOB_POSITIONS.DETAILS, jobPositionId],
        exact: true,
      });
    }
  }, [jpIdToPutOffline, queryClient, setIsReferralModalOpen, resetPutOfflineFlow]);

  return {
    isPuttingOffline,
    isSubmittingFeedback,
    isConfirmationModalOpen,
    isNPSModalOpen,
    isSuccessModalOpen,
    isRecoveryModalOpen,
    isReferralModalOpen,
    jpIdToPutOffline,
    ppuPaidUntil,
    ppuOfflineScheduledAt,
    isLoadingPaidUntil,
    isSchedulingOffline,
    putOfflineMode,
    scheduledOfflineAt,
    isSubscriptionFeedbacksSubmitted,
    handleOpenPutOffline,
    handleConfirmPutOffline,
    handleConfirmScheduledPutOffline: supportsScheduledOffline ? handleConfirmScheduledPutOffline : undefined,
    handleSubmitNPSFeedback,
    handleCloseConfirmationModal,
    handleCloseNPSModal,
    handleCloseSuccessModal,
    handleCloseRecoveryModal,
    handleCloseReferralModal,
  };
}
